import { useCallback, useState } from 'react';
import { DATE_RANGE_PRESETS, isoDay, monthsAgoIso } from '../utils/dateRange';
import type { DateRangePreset } from '../utils/dateRange';

const DEFAULT_RANGE_MONTHS = 3;

export interface ResolvedDateRangePreset extends DateRangePreset {
    from: string;
    to: string;
}

export interface UseDateRangeOptions {
    /** Months the range spans on mount. Defaults to 3. */
    defaultMonths?: number;
    /** Preset buttons, newest-first. Read once on mount; later changes are ignored. */
    presets?: readonly DateRangePreset[];
}

export interface DateRangeState {
    /** Inclusive bounds as 'YYYY-MM-DD'; either may be '' once cleared. */
    from: string;
    to: string;
    setFrom: (value: string) => void;
    setTo: (value: string) => void;
    presets: readonly ResolvedDateRangePreset[];
    activePresetLabel: string | null;
    applyPreset: (months: number) => void;
    /** True when the start date is after the end date. */
    isInvalid: boolean;
}

/** From/to day strings with preset shortcuts. Pair with `DateRangeField` to render it. */
export function useDateRange(options: UseDateRangeOptions = {}): DateRangeState {
    const { defaultMonths = DEFAULT_RANGE_MONTHS, presets = DATE_RANGE_PRESETS } = options;
    const [from, setFrom] = useState(() => monthsAgoIso(defaultMonths));
    const [to, setTo] = useState(() => isoDay(new Date()));

    // Resolved once per mount, whether or not the caller memoised `presets`:
    // recomputing per render would silently deselect the highlighted preset if
    // the surface stays open across midnight.
    const [resolvedPresets] = useState<readonly ResolvedDateRangePreset[]>(() =>
        presets.map((preset) => ({
            ...preset,
            from: monthsAgoIso(preset.months),
            to: isoDay(new Date())
        }))
    );

    const applyPreset = useCallback((months: number) => {
        setFrom(monthsAgoIso(months));
        setTo(isoDay(new Date()));
    }, []);

    const activePreset = resolvedPresets.find((p) => from === p.from && to === p.to);

    return {
        from,
        to,
        setFrom,
        setTo,
        presets: resolvedPresets,
        activePresetLabel: activePreset?.label ?? null,
        applyPreset,
        isInvalid: Boolean(from && to && from > to)
    };
}
